import L from "leaflet";
import type { Polygon } from "geojson";
import * as turf from "@turf/turf";
import { ConfirmModalRender } from "../../../../implementations/modal/ConfirmModalRender";
import { AoiDrawEngine, AoiHerramienta } from "./AoiDrawEngine";
import {
  AoiSnapEditEngine,
  type OperacionVerticeAoi,
} from "./AoiSnapEditEngine";
import { AoiWmsLayer } from "./AoiWmsLayer";
import {
  fx_esLaMismaPieza,
  PoligonosPadreWfsService,
} from "./PoligonosPadreWfsService";
import { VerticesPoligonoWfsService } from "./VerticesPoligonoWfsService";

/** Toolbar de dibujo del AOI (paso 3): Dibujar / Editar / Borrar / Unión.
 *  Es solo la UI y la orquestación — el dibujo lo resuelve `AoiDrawEngine`,
 *  igual que en la app de referencia externa (`referencias_codigo_externo/App`)
 *  la barra (`BarraDibujo.tsx`) no conoce el motor (`dibujoMapa.ts`).
 *
 *  **Ya no usa `leaflet-draw`.** Antes envolvía `L.Draw.Polygon` /
 *  `L.EditToolbar.Edit` / `L.EditToolbar.Delete`; con eso se fueron también
 *  el parche de `GeometryUtil.readableArea` (bug de leaflet-draw@1.0.4 con
 *  esbuild en modo estricto), la traducción de `L.drawLocal` y el recreado
 *  forzado de los handlers de editar/borrar tras cada ciclo (estado interno
 *  corrupto, bug conocido del plugin). Nada de eso le hacía falta al paso 4,
 *  que sí sigue usando leaflet-draw (`LoteSplitToolbar`, `L.Draw.Polyline`).
 *  También se fue el hack de `turf.booleanPointInPolygon` sobre el click del
 *  MAPA para detectar qué polígono borrar: con capas propias y renderer SVG,
 *  `layer.on("click")` dispara siempre.
 *
 *  Comportamiento tipo radio (mismo patrón que `MeasureControl`): activar un
 *  botón apaga los otros; Editar y Borrar arrancan deshabilitados y se
 *  habilitan en cuanto hay al menos un polígono. Tras crear/editar/borrar
 *  recalcula la unión topológica (`AoiPolygonUnion`). El borrado pide
 *  confirmación antes de ejecutarse (pedido explícito del paso 3; la
 *  referencia también confirma). */
/** Qué acompaña a una notificación de cambio de piezas. */
export type NotificacionPiezas = {
  /** `true` = la geometría que cambió YA está guardada en la base (la escribió
   *  `EditarVerticePoligonoPadre`). Quien escucha no tiene que encolar un
   *  `GuardarAoi` por esto; sí le queda pendiente el perímetro, que es la unión
   *  con puentes y sigue siendo del cliente. */
  geometriaGuardada?: boolean;
};

export class AoiDrawToolbar {
  /** Registra los botones en el contenedor y arma el motor de dibujo. */
  /** @param wms Capa WMS del paso 3. Es la que pinta las piezas guardadas, los
   *   VÉRTICES de la pieza que se está editando y el rojo de la elegida para
   *   borrar. **El trazo en curso ya no sale de acá** (2026-10-23): lo dibuja
   *   Leaflet, dentro del motor. `null` = sin registro en curso: no se ven los
   *   vértices, y el modo Editar cae en la geometría del modelo.
   *  @param padresWfs Lectura de la geometría de una pieza (gRPC). Quedó como
   *   el camino DEGRADADO del modo Editar: la fuente normal son los vértices
   *   (`VerticesPoligonoWfsService`), y esto sirve cuando la pieza todavía no
   *   los tiene extraídos. `null` = se edita la copia del modelo.
   *  @param registroId Registro en curso: el servidor solo devuelve los vértices
   *   de una pieza si el registro es del cliente. `null` = sin registro, el modo
   *   Editar cae en la geometría del modelo. */
  init(
    parent: HTMLElement,
    map: L.Map,
    wms: AoiWmsLayer | null = null,
    padresWfs: PoligonosPadreWfsService | null = null,
    registroId: string | null = null,
  ): void {
    this._map = map;
    this._wms = wms;
    this._padresWfs = padresWfs;
    this._registroId = registroId;
    this._engine = new AoiDrawEngine({
      map,
      onDibujado: this._onDibujado,
      onEditado: this._onEditado,
      onPedirEdicion: this._onPedirEdicion,
      onPedirBorrado: this._onPedirBorrado,
      onCapasCambio: this._refreshAvailability,
      // Indirecto a propósito: el handler se registra ANTES de `init()` (ver
      // `setOnSolapeBloqueado`), que es donde se crea el motor.
      onSolapeBloqueado: () => this._onSolapeBloqueado?.(),
    });
    // Dibujar/Editar/Borrar son las acciones principales del paso — botón
    // grande con label (`sx-map-btn--lg`), a diferencia de zoom, que queda
    // como ícono chico.
    this._drawBtn = this._makeButton("ph-polygon", "Dibujar", this._onDrawClick);
    this._editBtn = this._makeButton(
      "ph-pencil-simple",
      "Editar",
      this._onEditClick,
    );
    this._deleteBtn = this._makeButton("ph-trash", "Borrar", this._onDeleteClick);
    parent.appendChild(this._drawBtn);
    parent.appendChild(this._editBtn);
    parent.appendChild(this._deleteBtn);
    // Botón "Unión" DESACTIVADO (pedido explícito): era una herramienta de
    // inspección para traer al frente la previsualización de la unión
    // topológica y revisar los puentes. No hay nada que previsualizar: la
    // unión ya ni se calcula acá, la hace el servidor al guardar
    // (`PerimetroGlobalGeometria`).
    // Se deja el código —comentado acá y en sus piezas— por si hace falta
    // volver a inspeccionar el algoritmo.
    // this._unionBtn = this._makeButton("ph-stack", "Unión", this._onUnionClick);
    // parent.appendChild(this._unionBtn);

    this._refreshAvailability();
    // Primer disparo incondicional (no solo desde loadExisting/onDibujado/...):
    // así quien escuche `setOnPiezasChange` tiene un valor desde el arranque,
    // aunque todavía no haya nada dibujado (ej. badge de hectáreas en 0).
    this._notifyPiezasChange();
    this._onToolChange?.(null);
  }

  /** Limpia el motor y los listeners de los botones. */
  dispose(): void {
    if (!this._map) return;

    this._snapEdit?.dispose();
    this._snapEdit = null;
    this._soltarPiezaEnEdicion();
    this._engine?.destruir();
    this._engine = null;

    if (this._drawBtn) L.DomEvent.off(this._drawBtn, "click", this._onDrawClick);
    if (this._editBtn) L.DomEvent.off(this._editBtn, "click", this._onEditClick);
    if (this._deleteBtn)
      L.DomEvent.off(this._deleteBtn, "click", this._onDeleteClick);
    // if (this._unionBtn) L.DomEvent.off(this._unionBtn, "click", this._onUnionClick);
    this._drawBtn = null;
    this._editBtn = null;
    this._deleteBtn = null;
  }

  /** Notifica cada vez que cambia si hay o no al menos un polígono dibujado
   *  (habilita/deshabilita Editar/Borrar) — lo usa `RegistroFincaPaso3Render`
   *  para habilitar "Continuar" solo cuando hay algo que guardar, y
   *  `AoiMapRender` para decidir si muestra el buscador. */
  setOnAvailabilityChange(handler: (hasShapes: boolean) => void): void {
    this._onAvailabilityChange = handler;
  }

  /** Notifica las piezas dibujadas (sin unir) en cada crear/editar/borrar, y
   *  en `loadExisting` al restaurar. Lo usa `RegistroFincaPaso3Render` para
   *  guardarlas en el borrador y recalcular el área en vivo contra el máximo
   *  de hectáreas del plan.
   *
   *  Manda las PIEZAS y no la geometría unificada a propósito: unir es caro y
   *  ahora solo pasa al pulsar "Continuar" (ver `calcularGeometriaFinal`). El
   *  área sale de sumar las piezas, que da lo mismo porque el motor ya no
   *  permite que se solapen. */
  setOnPiezasChange(
    handler: (piezas: Polygon[], opciones?: NotificacionPiezas) => void,
  ): void {
    this._onPiezasChange = handler;
  }

  /** Quién persiste una operación de vértice del modo Editar.
   *
   *  Recibe la pieza y la operación (un punto, no la geometría) y devuelve la
   *  geometría que quedó GUARDADA, o `null` si el servidor la rechazó. La
   *  toolbar escribe en el modelo lo que devuelva: la autoridad es la base.
   *
   *  Sin este handler —o si la pieza todavía no tiene id— la edición cae al
   *  camino viejo: se escribe el modelo y el guardado sale por `GuardarAoi`
   *  con todas las piezas. */
  setOnEditarVertice(
    handler: (
      poligonoPadreId: string,
      operacion: OperacionVerticeAoi,
    ) => Promise<Polygon | null>,
  ): void {
    this._onEditarVertice = handler;
  }

  /** Notifica qué herramienta quedó activa (o `null`). Lo usa `AoiMapRender`
   *  para tres cosas que son suyas porque vive ahí el DOM: pintar de blanco
   *  los contornos de referencia mientras se dibuja, mostrar el aviso de ayuda
   *  y ocultar el buscador. Reemplaza a los eventos `L.Draw.Event.DRAWSTART`/
   *  `DRAWSTOP` que se escuchaban antes, que se fueron con leaflet-draw. */
  setOnToolChange(handler: (tool: AoiHerramienta | null) => void): void {
    this._onToolChange = handler;
  }

  /** Notifica que se rechazó algo por pisar otra pieza. Lo usa `AoiMapRender`
   *  para mostrarlo en la fila de avisos: sin el aviso, un rechazo se ve como
   *  "no pasó nada" — la forma vuelve sola a donde estaba y no se entiende por
   *  qué. Hay que registrarlo ANTES de `init()`: ahí se crea el motor, que es
   *  uno de los que lo dispara.
   *
   *  El `motivo` es opcional porque hay dos clases de rechazo con el mismo
   *  destino: los del dibujo, que son siempre el mismo caso y usan el texto por
   *  defecto del aviso, y los de la EDICIÓN de vértices, que traen su propio
   *  texto (quedó pisando una pieza, quedó con menos de tres esquinas). */
  setOnSolapeBloqueado(handler: (motivo?: string) => void): void {
    this._onSolapeBloqueado = handler;
  }

  /** Los polígonos dibujados, uno por uno. Es lo ÚNICO que viaja al guardar:
   *  el perímetro unido lo calcula el servidor con ellos
   *  (`PerimetroGlobalGeometria`). */
  getIndividualGeometries(): Polygon[] {
    return this._engine?.piezas.geometrias() ?? [];
  }

  /** Los polígonos dibujados, cada uno con si fue modificado: `false` solo
   *  para un polígono restaurado (`loadExisting`) que no se editó; uno nuevo
   *  o editado con la herramienta Editar va en `true`. */
  getPiezas(): { polygon: Polygon; modificada: boolean }[] {
    return (this._engine?.piezas.piezas() ?? []).map((pieza) => ({
      polygon: pieza.polygon,
      modificada: pieza.modificada,
    }));
  }

  /** El id de cada pieza en `tbl_temp_poligonos_padre`, en el orden en que
   *  viajan al guardado. Cadena vacía = pieza nueva. Ver `AoiPieza.id`. */
  getPiezasIds(): string[] {
    return this._engine?.piezas.ids() ?? [];
  }

  /** Adopta los ids que devolvió el guardado (ver
   *  `AoiPiezasState.adoptarIds`). Lo llama el paso 3 con la respuesta del
   *  `GuardarAoi`: es lo que le da a una pieza recién dibujada el id con el que
   *  después se le piden los vértices. */
  adoptarIdsDePiezas(ids: readonly string[]): void {
    this._engine?.piezas.adoptarIds(ids);
  }

  /** Carga piezas que ya existen al montar el paso. Debe llamarse después de
   *  `init()`.
   *
   *  @param enLaBase si esas piezas ya están en `poligonos_padre` y entonces las
   *   pinta el WMS, o si todavía no (KML del paso 2, piezas reconstruidas de los
   *   lotes) y las tiene que pintar Leaflet hasta el primer guardado. Ver
   *   `AoiDrawEngine.cargarExistentes`. */
  loadExisting(
    polygons: Polygon[],
    enLaBase: boolean,
    /** Ver `AoiDrawEngine.cargarExistentes`. */
    ids: readonly string[] = [],
  ): void {
    this._engine?.cargarExistentes(polygons, enLaBase, ids);
    this._notifyPiezasChange();
  }

  /** Las piezas quedaron guardadas: Leaflet suelta las capas que las venían
   *  pintando y de acá en más las pinta el WMS (ver
   *  `AoiDrawEngine.confirmarPersistencia`).
   *
   *  También se repintan los VÉRTICES, si hay una pieza en edición. Hace falta
   *  porque los vértices los dibuja GeoServer desde la base, y los re-extrae el
   *  servidor DENTRO del guardado: mientras el guardado viajaba, la base
   *  todavía tenía el vértice en su posición anterior. Recién ahora coincide.
   *
   *  Es el único repintado de los vértices por operación, y va acá por eso
   *  mismo: pedirlo al mover el vértice traería la forma vieja, y después
   *  habría que volver a pedir todo cuando el guardado vuelve. */
  confirmarPersistencia(): void {
    this._engine?.confirmarPersistencia();
    if (this._piezaEnEdicion !== null) {
      this._mostrarVerticesDe(this._piezaEnEdicion);
    }
  }

  private _map: L.Map | null = null;
  private _engine: AoiDrawEngine | null = null;
  private _wms: AoiWmsLayer | null = null;
  private _padresWfs: PoligonosPadreWfsService | null = null;
  private _onEditarVertice:
    | ((
        poligonoPadreId: string,
        operacion: OperacionVerticeAoi,
      ) => Promise<Polygon | null>)
    | null = null;

  private _drawBtn: HTMLButtonElement | null = null;
  private _editBtn: HTMLButtonElement | null = null;
  private _deleteBtn: HTMLButtonElement | null = null;
  // private _unionBtn: HTMLButtonElement | null = null;
  // private _unionAlFrente = false;

  private _onAvailabilityChange: ((hasShapes: boolean) => void) | null = null;
  private _onPiezasChange:
    | ((piezas: Polygon[], opciones?: NotificacionPiezas) => void)
    | null = null;
  private _onToolChange: ((tool: AoiHerramienta | null) => void) | null = null;
  /** La herramienta activa, para poder responder `esperandoPiezaParaEditar`. */
  private _activa: AoiHerramienta | null = null;
  private _onSolapeBloqueado: ((motivo?: string) => void) | null = null;

  private _onDrawClick = (): void => this._toggleTool("draw");
  private _onEditClick = (): void => this._toggleTool("edit");
  private _onDeleteClick = (): void => this._toggleTool("delete");

  /** Se cerró un polígono nuevo. La herramienta de dibujar QUEDA ACTIVA para
   *  encadenar el siguiente (comportamiento de la referencia; antes el paso 3
   *  se apagaba solo tras cada forma), así que no se toca `_setActiveTool`. */
  private _onDibujado = (): void => this._notifyPiezasChange();

  private _onEditado = (): void => this._notifyPiezasChange();

  /** La pieza que el motor de snap está editando, o `null`. */
  private _piezaEnEdicion: number | null = null;

  /** El estado de la pieza ANTES de la última edición de vértices, para poder
   *  deshacerla si el guardado la rechaza. `null` = no hay nada que deshacer. */
  private _ultimaEdicion: {
    indice: number;
    polygon: Polygon;
    modificada: boolean;
  } | null = null;

  private _snapEdit: AoiSnapEditEngine | null = null;

  /** Lectura de los vértices de una pieza. No guarda estado: la pieza va por
   *  parámetro en cada llamada. */
  private readonly _verticesWfs = new VerticesPoligonoWfsService();
  /** Registro en curso (lo pasa `init`), para leer los vértices de una pieza. */
  private _registroId: string | null = null;

  /** Click sobre una pieza en modo Editar.
   *
   *  **Es un toggle**: si la pieza clickeada ya era la que estaba en edición, se
   *  suelta y sus vértices se apagan.
   *
   *  Lo que hace al seleccionar:
   *   1. prende la capa WMS de los VÉRTICES de esa pieza, filtrada por su id;
   *   2. los pide también por gRPC y se los entrega al motor de snap, que de ahí
   *      saca el handle que pone al vuelo bajo el cursor — uno solo, el del
   *      punto enganchado.
   *
   *  **Lo que ya NO hace.** No pinta la pieza de celeste y no vuelve a crear su
   *  geometría en Leaflet.
   *
   *  El celeste era un GetMap de la pieza entera por cada click de selección, y
   *  lo único que decía es "ésta es la que estás editando" — que es justo lo que
   *  dicen sus vértices, ahora que se ven. La copia invisible en Leaflet
   *  (`_capaEnEdicion`) era la misma geometría por segunda vez: se mantenía al
   *  día en cada movimiento de vértice y nadie la leía, porque lo que se guarda
   *  sale del modelo (`AoiPiezasState`) y lo que se dibuja sale del WMS.
   *
   *  No duplicar la geometría es también lo que hace que esto no pueda
   *  desincronizarse: hay UN modelo del lado del cliente y UNA fuente de
   *  vértices del lado del servidor. */
  private _onPedirEdicion = (indice: number): void => {
    if (!this._map) return;

    if (this._piezaEnEdicion === indice) {
      this._soltarPiezaEnEdicion();
      this._snapEdit?.cerrar();
      return;
    }

    const pieza = this._engine?.piezas.pieza(indice);
    if (!pieza) return;

    this._soltarPiezaEnEdicion();
    this._piezaEnEdicion = indice;
    this._mostrarVerticesDe(indice);
    // El aviso depende de si ya hay una pieza elegida: con varias piezas, pasa
    // de "de click en un lote" a "arrastre un vértice" en este momento.
    this._onToolChange?.(this._activa);

    void this._abrirEdicionAsync(indice, pieza);
  };

  /** Prende la capa WMS de vértices de una pieza, acotada a su caja. */
  private _mostrarVerticesDe(indice: number): void {
    const pieza = this._engine?.piezas.pieza(indice);
    const bbox = this._engine?.piezas.bbox(indice);
    if (!pieza?.id || !bbox) return;
    this._wms?.mostrarVertices(pieza.id, bbox);
  }

  /** Trae los vértices de la pieza y arranca la edición.
   *
   *  **El orden de preferencia es: vértices, geometría, modelo.**
   *   1. los vértices de `tbl_temp_vertices_dibujo`, que son la misma fuente
   *      que está dibujando el WMS — con eso los handles caen exactamente sobre
   *      los puntos que el usuario ve;
   *   2. si la pieza no tiene vértices extraídos (todavía no se guardó, o es un
   *      registro anterior a esa tabla), la geometría de la pieza por gRPC
   *      (`PoligonosPadreWfsService`), comparada con la del modelo para no
   *      editar una respuesta degradada — ver `fx_esLaMismaPieza`;
   *   3. si tampoco hay respuesta, la copia del modelo.
   *
   *  Los dos últimos son caminos degradados y están sólo para que el modo
   *  Editar no se quede sin nada que agarrar. */
  private async _abrirEdicionAsync(
    indice: number,
    pieza: { polygon: Polygon; id: string | null },
  ): Promise<void> {
    const geometria = await this._geometriaAEditarAsync(indice, pieza);
    // Mientras viajaba la lectura el usuario pudo soltar la pieza, cambiar de
    // herramienta o elegir otra: lo que vuelve ya no corresponde.
    if (this._piezaEnEdicion !== indice || !this._map) return;

    // No se escribe nada en el modelo acá, y es a propósito: `escribir()` marca
    // la pieza como MODIFICADA, y abrir una pieza en Editar sin tocarle un
    // vértice no la modifica. Marcarla haría que el servidor la tome como
    // cambiada y borre los lotes en los que ya estaba subdividida (ver
    // `ConfirmarPiezas`).

    if (!this._snapEdit) {
      this._snapEdit = new AoiSnapEditEngine({
        map: this._map,
        onCambio: this._onSnapCambio,
        onRechazado: (motivo) => this._onSolapeBloqueado?.(motivo),
        validar: this._validarEdicion,
      });
      this._snapEdit.activar();
    }
    this._snapEdit.seleccionar(geometria);
  }

  private async _geometriaAEditarAsync(
    indice: number,
    pieza: { polygon: Polygon; id: string | null },
  ): Promise<Polygon> {
    if (pieza.id) {
      const vertices = await this._verticesWfs.leerAsync(this._registroId, pieza.id);
      if (vertices) return VerticesPoligonoWfsService.aPoligono(vertices);
    }

    const leida = (await this._padresWfs?.leerAsync(indice)) ?? null;
    return leida && fx_esLaMismaPieza(leida, pieza.polygon)
      ? leida
      : pieza.polygon;
  }

  /** Suelta la pieza en edición: se apagan sus vértices. No toca el motor de
   *  snap — eso lo decide quien llama. */
  private _soltarPiezaEnEdicion(): void {
    if (this._piezaEnEdicion !== null) {
      this._wms?.ocultarVertices();
      this._piezaEnEdicion = null;
      // Soltarla devuelve el aviso a "de click en un lote", si la herramienta
      // sigue activa.
      this._onToolChange?.(this._activa);
    }
  }

  /** Chequeo PREVIO de la forma que dejó una operación de vértices, antes de
   *  mandarla al servidor.
   *
   *  **La validación de verdad es del servidor** (`EditarVerticePoligonoPadre`):
   *  mide el solape contra las piezas vecinas sobre la geometría guardada y
   *  dentro de su transacción, así que un rechazo no deja nada a medias. Antes
   *  eso se le preguntaba a PostGIS por WFS desde acá, con un POST que llevaba
   *  el polígono ENTERO en GML por cada vértice soltado — ver la nota de
   *  `PoligonosPadreWfsService.solapaConOtraPiezaAsync`, que quedó sin uso.
   *
   *  Acá sólo queda el auto-cruce, que se responde sin red. */
  private _validarEdicion = async (
    poligono: Polygon,
  ): Promise<string | null> => {
    if (this._piezaEnEdicion === null) return null;

    // **Lo único que se pregunta acá es el auto-cruce**, y se responde sin red.
    //
    // No es una relación con otra geometría, así que ningún filtro espacial lo
    // detecta; `turf.kinks` lo contesta al instante y evita mandar al servidor
    // una operación que seguro va a rechazar.
    //
    // **El solape contra las piezas vecinas ya NO se pregunta por WFS.** Lo
    // hacía un POST a GeoServer con el polígono ENTERO en GML, por cada vértice
    // que se soltaba — con un área importada de un KML, cientos de kB de ida
    // solo para preguntar. Ahora lo valida el mismo rpc que aplica el vértice
    // (`EditarVerticePoligonoPadre`), contra la geometría guardada y dentro de
    // su transacción: si no pasa, no se tocó nada. Es la misma pregunta, hecha
    // por quien ya tiene los datos a mano.
    try {
      if (turf.kinks(turf.polygon(poligono.coordinates)).features.length > 0) {
        return "Así el perímetro se cruzaría consigo mismo. Los tramos no pueden quedar en X.";
      }
    } catch {
      // Geometría que turf no pudo procesar: se deja pasar y la decide el
      // servidor, que es la autoridad.
    }

    return null;
  };

  /** Deshace la última edición de vértices porque el servidor la rechazó.
   *
   *  **Para qué.** Hay un rechazo que no se puede anticipar desde el cliente: el
   *  perímetro cruzado consigo mismo (los tramos en X). No es una relación con
   *  otra geometría, así que no hay filtro espacial que lo pregunte; lo decide
   *  el `IsValid` del `GuardarAoi`. Para entonces el vértice ya se movió en el
   *  modelo, así que la única forma de respetar el rechazo es volver atrás.
   *
   *  **Nada se perdió en la base**: el guardado falló, o sea que la geometría
   *  anterior sigue guardada y el WMS la sigue dibujando. Lo único desalineado
   *  era el modelo del cliente, y es lo que se devuelve a su lugar — incluida la
   *  marca de modificada, que si no quedaría puesta por una edición que nunca se
   *  aplicó.
   *
   *  Se vuelve a cargar la pieza en el motor de snap para que los handles queden
   *  sobre la forma buena, y se avisa el motivo: sin aviso, el vértice volviendo
   *  solo a su lugar no se entiende.
   *
   *  **Sí se notifica el cambio de piezas**, lo que encola otro guardado. Es a
   *  propósito: ese guardado manda la geometría anterior, que es la que el
   *  servidor ya tenía, así que no cambia nada en la base — y a cambio el badge
   *  de hectáreas vuelve a cuadrar. Sin notificar quedaría mostrando el área de
   *  una forma rechazada, que además es un número sin sentido (el área de un
   *  polígono cruzado no significa nada). */
  revertirEdicionRechazada(motivo: string): boolean {
    const previo = this._ultimaEdicion;
    // Se limpia PRIMERO: si el guardado de la reversa fallara también, no hay
    // que volver a deshacer nada — ya estamos en el estado anterior.
    this._ultimaEdicion = null;
    // **Devuelve si pudo deshacer.** Sin nada que deshacer, el modelo queda
    // diciendo algo que la base rechazó, y antes eso se iba en silencio: el
    // guardado siguiente volvía a mandar la misma geometría inválida y volvía a
    // fallar, para siempre. Quien llama tiene que recargar de la base
    // (`RegistroFincaPaso3Render`).
    if (!previo || !this._engine) return false;

    this._engine.restaurarPieza(previo.indice, previo.polygon, previo.modificada);

    if (this._piezaEnEdicion === previo.indice) {
      this._snapEdit?.seleccionar(previo.polygon);
    }

    this._onSolapeBloqueado?.(motivo);
    this._notifyPiezasChange();
    return true;
  }

  /** Pisa TODAS las piezas con las que hay en la base.
   *
   *  Es el camino de salida cuando el guardado rechazó algo que no se puede
   *  deshacer localmente: manda la base, nunca la memoria. Suelta además la
   *  pieza en edición y su motor de snap, porque los índices pueden haber
   *  cambiado y los handles quedarían sobre coordenadas que ya no existen.
   *
   *  Mismo criterio que el paso 4 (`LotesTempSync`: si algo falla, se deja de
   *  mandar y se recarga). */
  reemplazarPiezasDeLaBase(polygons: Polygon[], ids: readonly string[]): void {
    this._soltarPiezaEnEdicion();
    this._snapEdit?.cerrar();
    this._ultimaEdicion = null;
    this._engine?.cargarExistentes(polygons, true, ids);
    this._notifyPiezasChange();
  }

  /** Pinta en rojo la pieza elegida para borrar, acotado a su caja. Es el
   *  único resalte que queda: el celeste de la pieza en edición se fue junto
   *  con su SLD (ver `_onPedirEdicion`). */
  private _resaltarParaBorrar(indice: number): Promise<void> {
    const bbox = this._engine?.piezas.bbox(indice);
    if (!bbox || !this._wms) return Promise.resolve();
    return this._wms.resaltarParaBorrar(indice, bbox);
  }

  /** El motor de snap cambió la geometría: se movió, se agregó o se quitó un
   *  vértice.
   *
   *  El cambio se escribe en el MODELO, que es de donde `getPiezas()` lee, y el
   *  guardado sale por el mismo camino de siempre (`escribirPieza` →
   *  `onEditado` → `_notifyPiezasChange` → gRPC `GuardarAoi`), que es el que
   *  valida solapes y áreas contra el resto del registro. */
  private _onSnapCambio = (
    poligono: Polygon,
    operacion: OperacionVerticeAoi,
  ): void => {
    const indice = this._piezaEnEdicion;
    if (indice === null) return;

    // Con qué volver atrás si el servidor rechaza la forma. Se toma del modelo
    // ANTES de escribir, y se guarda también la marca de modificada: ver
    // `revertirEdicionRechazada`.
    const anterior = this._engine?.piezas.pieza(indice);
    this._ultimaEdicion = anterior
      ? {
          indice,
          polygon: anterior.polygon,
          modificada: anterior.modificada,
        }
      : null;

    // **El camino nuevo: viaja la OPERACIÓN, no la geometría.**
    //
    // El servidor aplica el vértice sobre la pieza guardada
    // (`EditarVerticePoligonoPadre`), valida que no quede cruzada ni pisando a
    // una vecina y devuelve la geometría que quedó en la base. Antes se
    // escribía el modelo acá y el guardado salía por `GuardarAoi` con TODAS las
    // piezas: con un área importada de un KML eso son cientos de kB por cada
    // vértice que se suelta, y el arrastre se sentía peor que cuando el paso 3
    // dibujaba con Leaflet.
    //
    // Se cae al camino viejo cuando la pieza todavía no tiene id en la base
    // (recién dibujada, sin guardar) o cuando la operación tocó un anillo
    // interior, que el servidor no sabe editar: ahí la autoridad sigue siendo
    // el `GuardarAoi` completo.
    const persistir = this._onEditarVertice;
    const piezaId = anterior?.id ?? null;
    if (persistir && piezaId && operacion.anillo === 0) {
      void persistir(piezaId, operacion).then((guardada) => {
        // Mientras viajaba la respuesta el usuario pudo soltar la pieza o
        // elegir otra: lo que vuelve ya no corresponde.
        if (this._piezaEnEdicion !== indice) return;

        if (!guardada) {
          // El servidor no aplicó nada: el modelo nunca se tocó, así que
          // alcanza con devolver los handles a la forma guardada.
          const previa = this._ultimaEdicion?.polygon;
          this._ultimaEdicion = null;
          if (previa) this._snapEdit?.seleccionar(previa);
          return;
        }

        // `restaurarPieza` y no `escribirPieza` a propósito: escribir dispara
        // `onEditado` → `_notifyPiezasChange` → otro `GuardarAoi`, que es
        // justamente lo que este camino viene a evitar. La marca de modificada
        // se pone a mano (`true`): la pieza SÍ cambió, y de eso depende que
        // `ConfirmarPiezas` descarte sus lotes.
        this._engine?.restaurarPieza(indice, guardada, true);
        this._ultimaEdicion = null;
        // Los handles pasan a la forma de la base, que puede no ser exactamente
        // la que dejó el arrastre (el servidor redondea a 7 decimales y puede
        // haberle sellado un vértice a una pieza vecina).
        this._snapEdit?.seleccionar(guardada);
        // La pieza ya está en la base: se avisa con `geometriaGuardada` para
        // que el paso 3 actualice el área en vivo y marque que falta guardar el
        // perímetro, SIN encolar un `GuardarAoi` por este vértice.
        this._notifyPiezasChange({ geometriaGuardada: true });
        // Los vértices los dibuja GeoServer leyendo la base, y la base YA
        // cambió: ahora sí tiene sentido pedirlos de nuevo. El repintado de la
        // PIEZA (la otra capa) lo pide el paso 3 al volver de su rpc, que es
        // quien ya tiene ese camino armado (`refrescarPoligonosPadre`).
        this._mostrarVerticesDe(indice);
      });
      return;
    }

    // Camino viejo (pieza sin id en la base): el cambio se escribe en el
    // MODELO, que es de donde `getPiezas()` lee, y el guardado sale por
    // `escribirPieza` → `onEditado` → `_notifyPiezasChange` → `GuardarAoi`.
    // No hay escritura directa de la pieza: `vw_poligonos_padre` es una vista
    // de solo lectura y la que escribe es el servidor.
    //
    // Acá NO se repinta la capa de vértices: la base todavía tiene el vértice
    // donde estaba —el guardado recién se encoló— y los puntos volverían
    // viejos. Se repintan cuando el guardado vuelve (`confirmarPersistencia`).
    // Tampoco se llama a `_notifyPiezasChange`: ya lo disparó `escribirPieza`.
    this._engine?.escribirPieza(indice, poligono);
  };

  /** Click sobre un lote en modo borrar (una pieza del área, en los nombres
   *  internos): se resalta en rojo y se pide confirmación antes de quitarlo. El motor nunca borra por su cuenta.
   *
   *  El rojo lo pinta el WMS con `sextante_poligono_padre_eliminar`, que es el
   *  port del `DELETE_SELECTED_STYLE` que antes se le aplicaba a la capa.
   *
   *  **El modal espera al resalte.** El color viene de GeoServer, así que
   *  tarda una ida y vuelta; el modal es inmediato. Abriéndolos a la vez salía
   *  primero el modal y el rojo aparecía después, debajo: el usuario tenía que
   *  decidir sin ver qué pieza iba a borrar. Ahora el modal se abre cuando los
   *  tiles del resalte ya están en pantalla (o a los 800 ms, si GeoServer no
   *  contesta — ver `AoiWmsLayer.resaltarParaBorrar`).
   *
   *  Si mientras tanto el usuario cambió de herramienta o se soltó el paso, no
   *  se abre nada: el resalte se limpia y listo. */
  private _onPedirBorrado = (indice: number): void => {
    void this._resaltarParaBorrar(indice).then(() => {
      if (this._engine?.herramienta !== "delete") {
        this._wms?.limpiarResalte();
        return;
      }
      this._abrirModalDeBorrado(indice);
    });
  };

  private _abrirModalDeBorrado(indice: number): void {
    new ConfirmModalRender({
      title: "Eliminar lote",
      message:
        "¿Eliminar este lote del área de la finca? Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      onConfirm: async () => {
        this._wms?.limpiarResalte();
        // Si la pieza borrada era la que estaba en edición, hay que soltarla:
        // los índices se corren y el motor de snap quedaría editando otra.
        if (this._piezaEnEdicion !== null) {
          this._soltarPiezaEnEdicion();
          this._snapEdit?.cerrar();
        }
        // La caja de la pieza se guarda ANTES de borrarla: después ya no hay de
        // dónde sacarla, y es lo que el repintado del WMS necesita para cubrir
        // el hueco que deja (ver `tomarZonaLiberada`).
        this._zonaLiberada = this._engine?.piezas.bbox(indice) ?? null;
        this._engine?.borrar(indice);
        this._notifyPiezasChange();
      },
      onCancel: () => this._wms?.limpiarResalte(),
    }).init();
  }

  private _refreshAvailability = (): void => {
    const hasShapes = (this._engine?.piezas.total ?? 0) > 0;
    if (this._editBtn) this._editBtn.disabled = !hasShapes;
    if (this._deleteBtn) this._deleteBtn.disabled = !hasShapes;

    // Quedarse sin polígonos con Editar o Borrar activos dejaría el botón
    // deshabilitado pero el modo encendido — hay que soltarlo.
    const tool = this._engine?.herramienta ?? null;
    if (!hasShapes && (tool === "edit" || tool === "delete")) {
      this._engine?.desactivar();
      this._setActiveTool(null);
    }

    this._onAvailabilityChange?.(hasShapes);
  };

  // /** Botón de inspección: trae el preview azul de la unión topológica al
  //  *  frente (normalmente va al fondo, detrás de los polígonos dibujados)
  //  *  para poder revisar cómo quedaron los puentes que arma el algoritmo. No
  //  *  toca la geometría ni lo que se guarda — es solo orden de dibujo. */
  // private _onUnionClick = (): void => {
  //   this._unionAlFrente = !this._unionAlFrente;
  //   this._union.setAlFrente(this._unionAlFrente);
  //   this._unionBtn?.classList.toggle("sx-map-btn--active", this._unionAlFrente);
  // };

  private _notifyPiezasChange(opciones?: NotificacionPiezas): void {
    this._onPiezasChange?.(this.getIndividualGeometries(), opciones);
  }

  private _makeButton(
    icon: string,
    title: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = L.DomUtil.create(
      "button",
      "sx-map-btn sx-map-btn--lg",
    ) as HTMLButtonElement;
    btn.innerHTML = `<i class="ph ${icon}"></i><span>${title}</span>`;
    btn.title = title;
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", onClick);
    return btn;
  }

  private _toggleTool(tool: AoiHerramienta): void {
    if (!this._engine) return;
    // Editar y Borrar no tienen sentido sin nada dibujado (sus botones ya
    // están deshabilitados; esto cubre el camino por teclado).
    if (tool !== "draw" && this._engine.piezas.total === 0) return;

    const activa = this._engine.activar(tool);
    // Al salir de Editar se sueltan la pieza, su resalte y sus handles: si no,
    // quedarían tres marcadores en el mapa respondiendo a una herramienta
    // apagada, y la pieza pintada de celeste sin estar en edición.
    if (activa !== "edit") {
      this._snapEdit?.cerrar();
      this._soltarPiezaEnEdicion();
    }
    // Y al salir de Borrar se limpia el rojo, si había quedado uno.
    if (activa !== "delete") this._wms?.limpiarResalte();

    // **Con UNA sola pieza, Editar entra directo a sus vértices.** Pedir un
    // click para elegir "cuál" cuando no hay alternativa es un paso de más: el
    // usuario ya dijo qué quiere editar al pulsar el botón. Con dos o más sí
    // hay que preguntar, y ahí el aviso lo pide (ver `AVISOS` de
    // `AoiMapRender`).
    //
    // Va ANTES de `_setActiveTool` a propósito: ese es el que avisa del cambio
    // de herramienta, y quien escucha arma el aviso según si ya hay una pieza en
    // edición. Al revés, el primer aviso diría "de click en un lote" un instante
    // antes de corregirse.
    // La herramienta se registra ANTES de auto-seleccionar: `_onPedirEdicion`
    // avisa del cambio de aviso leyendo `_activa`, y sin esto esa notificación
    // saldría con la herramienta anterior. `_setActiveTool`, más abajo, la
    // vuelve a escribir con el mismo valor.
    this._activa = activa;
    if (
      activa === "edit" &&
      this._piezaEnEdicion === null &&
      this._engine.piezas.total === 1
    ) {
      this._onPedirEdicion(1);
    }

    this._setActiveTool(activa);
  }

  /** La caja de la pieza que se acaba de borrar, UNA sola vez (se consume al
   *  leerla).
   *
   *  **Para qué.** El repintado del WMS está acotado al área que cambió, y al
   *  borrar una pieza esa área es la que la pieza OCUPABA — que no está dentro
   *  del perímetro nuevo. Sin esto, el repintado pedía sólo los tiles del área
   *  que queda y los de la pieza borrada no se volvían a pedir nunca: el
   *  polígono seguía viéndose aunque ya no estuviera en la base. Con dos piezas,
   *  borrar una dejaba las dos en pantalla.
   *
   *  Borrar la ÚLTIMA pieza no pasaba por esto y funcionaba: ahí el perímetro
   *  queda nulo y el repintado es total. */
  tomarZonaLiberada(): [number, number, number, number] | null {
    const zona = this._zonaLiberada;
    this._zonaLiberada = null;
    return zona;
  }

  private _zonaLiberada: [number, number, number, number] | null = null;

  /** ¿El modo Editar está esperando que el usuario elija una pieza?
   *
   *  Lo pregunta `AoiMapRender` para saber qué aviso mostrar: con una sola pieza
   *  nunca espera —entra directo—, y con varias espera hasta el click. */
  esperandoPiezaParaEditar(): boolean {
    return this._activa === "edit" && this._piezaEnEdicion === null;
  }

  private _setActiveTool(tool: AoiHerramienta | null): void {
    this._activa = tool;
    this._drawBtn?.classList.toggle("sx-map-btn--active", tool === "draw");
    this._editBtn?.classList.toggle("sx-map-btn--active", tool === "edit");
    this._deleteBtn?.classList.toggle("sx-map-btn--active", tool === "delete");
    this._onToolChange?.(tool);
  }
}
